import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface NotificationPrefs {
  emailMentions: boolean;
  emailAssignments: boolean;
  emailDue: boolean;
  emailWeeklyDigest: boolean;
  pushMentions: boolean;
  pushAssignments: boolean;
  pushDue: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string; // "HH:MM"
  quietHoursEnd: string;
}

export interface Profile {
  displayName: string;
  email: string;
  avatarColor: string;  // gradient tail
  timezone: string;
  locale: string;
  jobTitle?: string;
}

interface SettingsState {
  profile: Profile;
  notifications: NotificationPrefs;
  twoFactorEnabled: boolean;
  apiKey?: string;

  setProfile: (patch: Partial<Profile>) => void;
  setNotifications: (patch: Partial<NotificationPrefs>) => void;
  setTwoFactor: (enabled: boolean) => void;
  generateApiKey: () => string;
  revokeApiKey: () => void;
  reset: () => void;
}

function defaultProfile(): Profile {
  return {
    displayName: 'Alona',
    email: 'alona@playo.ai',
    avatarColor: 'from-neon-pink to-neon-purple',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    locale: navigator?.language ?? 'en-US',
    jobTitle: 'Founder',
  };
}

function defaultNotifications(): NotificationPrefs {
  return {
    emailMentions: true, emailAssignments: true, emailDue: true, emailWeeklyDigest: true,
    pushMentions: true, pushAssignments: false, pushDue: true,
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  };
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      profile: defaultProfile(),
      notifications: defaultNotifications(),
      twoFactorEnabled: false,

      setProfile: (patch) => set((s) => ({ profile: { ...s.profile, ...patch } })),
      setNotifications: (patch) => set((s) => ({ notifications: { ...s.notifications, ...patch } })),
      setTwoFactor: (enabled) => set({ twoFactorEnabled: enabled }),
      generateApiKey: () => {
        const key = 'myday_' + Array.from({ length: 32 }, () => Math.random().toString(36)[2]).join('');
        set({ apiKey: key });
        return key;
      },
      revokeApiKey: () => set({ apiKey: undefined }),

      reset: () => set({
        profile: defaultProfile(),
        notifications: defaultNotifications(),
        twoFactorEnabled: false,
        apiKey: undefined,
      }),
    }),
    { name: 'myday-settings', version: 1 },
  ),
);
